import React, { useState, useCallback, useMemo } from 'react';
import { TeamCardMini } from '../components/TeamCard';
import { TeamLogo } from '../components/TeamLogo';
import { MatchPitchGraphics } from '../components/MatchPitchGraphics';
import { MatchEvents } from '../components/MatchEvents';
import { TacticalPitch } from '../components/TacticalPitch';
import { SubstitutionModal } from '../components/SubstitutionModal';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { quickSimulate, fullSimulate, makeSubstitution } from '../utils/simulationEngine';
import { getTeamOverall, FORMATIONS } from '../utils/gameData';
import { buildChampionsLeagueTeams, buildEuropaLeagueTeams, buildConferenceLeagueTeams, buildCWCTeams } from '../utils/realTeams';
import { buildAllNationalTeams } from '../utils/worldCup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Lightning, Play, Pause, ArrowsLeftRight, Strategy, Timer, ArrowLeft, ShareNetwork, MagnifyingGlass } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useDebounceClick } from '../hooks/useDebounceClick';
import { downloadHighlightCard } from '../utils/shareCard';

const POOL_LABELS = {
  ewfl:   { l: 'EWFL',    c: '#C8102E' },
  ucl:    { l: 'ASIAN',   c: '#CCFF00' },
  uel:    { l: 'ASIAN',   c: '#CCFF00' },
  uecl:   { l: 'ASIAN',   c: '#CCFF00' },
  cwc:    { l: 'CWC',     c: '#007AFF' },
  nation: { l: 'NATIONS', c: '#FFD700' },
};

export const PlayNowPage = ({ teams, wcTeams = [], onUpdateTeams }) => {
  const navigate = useNavigate();
  const [homeTeam, setHomeTeam] = useState(null);
  const [awayTeam, setAwayTeam] = useState(null);
  const [homePool, setHomePool] = useState('ewfl');
  const [awayPool, setAwayPool] = useState('ewfl');
  const [homeSearch, setHomeSearch] = useState('');
  const [awaySearch, setAwaySearch] = useState('');
  const [matchState, setMatchState] = useState('setup');
  const [events, setEvents] = useState([]);
  const [currentMinute, setCurrentMinute] = useState(0);
  const [simSpeed, setSimSpeed] = useState([80]);
  const [simMode, setSimMode] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subTeam, setSubTeam] = useState(null);
  const [subsUsed, setSubsUsed] = useState({ home: 0, away: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const simControlRef = React.useRef(null);

  // Build (or memoize) the team pools that aren't already passed in.
  // EWFL teams come from props (live league state). All others are
  // derived from the same data files used by the matching pages so the
  // logos, ratings, and squads stay consistent.
  const uclTeams    = useMemo(() => buildChampionsLeagueTeams(),  []);
  const uelTeams    = useMemo(() => buildEuropaLeagueTeams(),     []);
  const ueclTeams   = useMemo(() => buildConferenceLeagueTeams(), []);
  const cwcTeams    = useMemo(() => buildCWCTeams(),              []);
  const nationTeams = useMemo(
    () => buildAllNationalTeams(teams, wcTeams && wcTeams.length ? wcTeams : null),
    [teams, wcTeams]
  );

  const getPool = useCallback((p) => {
    if (p === 'ewfl')   return teams       || [];
    if (p === 'ucl')    return uclTeams    || [];
    if (p === 'uel')    return uelTeams    || [];
    if (p === 'uecl')   return ueclTeams   || [];
    if (p === 'cwc')    return cwcTeams    || [];
    if (p === 'nation') return nationTeams || [];
    return [];
  }, [teams, uclTeams, uelTeams, ueclTeams, cwcTeams, nationTeams]);

  const filterTeams = (list, q) => {
    const s = (q || '').trim().toLowerCase();
    if (!s) return list;
    return list.filter(t => (t.name || '').toLowerCase().includes(s) || (t.country || '').toLowerCase().includes(s) || (t.city || '').toLowerCase().includes(s));
  };

  const homeScore = events.filter(e => e.type === 'goal' && e.team === 'home').length;
  const awayScore = events.filter(e => e.type === 'goal' && e.team === 'away').length;

  // Compute match ratings from events (for share card top-performer panel)
  const computeMatchRatings = () => {
    const ratings = {};
    if (!homeTeam || !awayTeam) return ratings;
    [...(homeTeam.lineup || []), ...(awayTeam.lineup || [])].forEach((num) => {});
    [homeTeam, awayTeam].forEach((team) => {
      (team.lineup || []).forEach((num) => {
        const p = team.players.find(pp => pp.number === num);
        if (p) ratings[p.id] = 6.5 + Math.random() * 0.6;
      });
    });
    events.forEach((ev) => {
      if (ev.type === 'goal' && ev.player) {
        ratings[ev.player.id] = (ratings[ev.player.id] || 6.5) + 1.4;
        if (ev.assister) ratings[ev.assister.id] = (ratings[ev.assister.id] || 6.5) + 0.9;
      } else if (ev.type === 'yellow_card' && ev.player) {
        ratings[ev.player.id] = (ratings[ev.player.id] || 6.5) - 0.4;
      } else if (ev.type === 'red_card' && ev.player) {
        ratings[ev.player.id] = (ratings[ev.player.id] || 6.5) - 1.5;
      } else if (ev.type === 'save' && ev.player) {
        ratings[ev.player.id] = (ratings[ev.player.id] || 6.5) + 0.5;
      }
    });
    Object.keys(ratings).forEach(k => { ratings[k] = Math.max(3, Math.min(10, ratings[k])); });
    return ratings;
  };

  const handleShareCard = async () => {
    try {
      await downloadHighlightCard({
        homeTeam, awayTeam, homeScore, awayScore,
        matchRatings: computeMatchRatings(),
        events,
        competition: 'EWFL FRIENDLY',
      });
    } catch (e) {
      console.error('Share card failed', e);
    }
  };

  const handleQuickSim = useCallback(() => {
    if (!homeTeam || !awayTeam || matchState !== 'setup') return;
    setSimMode('quick'); setMatchState('live'); setEvents([]);
    setTimeout(() => {
      const result = quickSimulate(homeTeam, awayTeam);
      setEvents(result.events); setCurrentMinute(90); setMatchState('finished');
    }, 300);
  }, [homeTeam, awayTeam]);

  const handleFullSim = useCallback(() => {
    if (!homeTeam || !awayTeam || matchState !== 'setup') return;
    if (simControlRef.current) { simControlRef.current.stop(); simControlRef.current = null; }
    setSimMode('full'); setMatchState('live'); setEvents([]); setCurrentMinute(0); setIsPaused(false);
    const control = fullSimulate(homeTeam, awayTeam,
      (event) => { if (event.type === 'minute') setCurrentMinute(event.minute); else setEvents(prev => [...prev, event]); },
      () => { setCurrentMinute(90); setMatchState('finished'); },
      simSpeed[0]
    );
    simControlRef.current = control;
  }, [homeTeam, awayTeam, simSpeed, matchState]);

  const togglePause = () => {
    if (!simControlRef.current) return;
    if (isPaused) { simControlRef.current.resume(); setIsPaused(false); }
    else { simControlRef.current.pause(); setIsPaused(true); }
  };

  const handleSub = (playerOutNum, playerInNum) => {
    if (!subTeam) return;
    // Ensure sim is paused before mutating
    if (simControlRef.current && !isPaused) {
      simControlRef.current.pause();
      setIsPaused(true);
    }
    const isHome = subTeam.id === homeTeam?.id;
    const teamToUpdate = isHome ? homeTeam : awayTeam;
    if (!teamToUpdate) return;
    
    // Build new team object
    const newLineup = teamToUpdate.lineup.map(n => n === playerOutNum ? playerInNum : n);
    const newBench = teamToUpdate.bench.map(n => n === playerInNum ? playerOutNum : n);
    const updated = { ...teamToUpdate, lineup: newLineup, bench: newBench };
    
    const playerOut = teamToUpdate.players.find(p => p.number === playerOutNum);
    const playerIn = teamToUpdate.players.find(p => p.number === playerInNum);

    // Use callback form to avoid stale state
    if (isHome) {
      setHomeTeam(updated);
      setSubsUsed(p => ({ ...p, home: p.home + 1 }));
    } else {
      setAwayTeam(updated);
      setSubsUsed(p => ({ ...p, away: p.away + 1 }));
    }
    
    setEvents(prev => [...prev, { type: 'substitution', minute: currentMinute, team: isHome ? 'home' : 'away', teamName: teamToUpdate.name, playerOut, playerIn, commentary: `Tactical change! ${playerOut?.name} makes way for ${playerIn?.name}.` }]);
  };

  const resetMatch = () => {
    setMatchState('setup'); setEvents([]); setCurrentMinute(0); setSubsUsed({ home: 0, away: 0 }); setSimMode(null); setIsPaused(false);
    if (simControlRef.current) { simControlRef.current.stop(); simControlRef.current = null; }
  };

  const debouncedQuick = useDebounceClick(handleQuickSim, 500);
  const debouncedFull = useDebounceClick(handleFullSim, 500);
  const debouncedPause = useDebounceClick(togglePause, 200);


  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="back-home-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">Play Now</h1>
            <p className="text-[#A0A0A5] mt-1">
              {matchState === 'live' && isPaused ? `PAUSED at ${currentMinute}'` : 'Quick match between any two teams'}
            </p>
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-[#FFD60A]/80 bg-[#FFD60A]/10 border border-[#FFD60A]/30 rounded-sm px-2 py-1 uppercase tracking-wider font-bold">
              <Lightning size={11} weight="fill" /> Friendly · Stats not saved to career
            </div>
          </div>
        </div>

        {/* Full sim pitch + pause */}
        {simMode === 'full' && matchState !== 'setup' && homeTeam && awayTeam && (
          <div className="mb-6 space-y-3">
            <MatchPitchGraphics homeTeam={homeTeam} awayTeam={awayTeam} events={events} currentMinute={currentMinute} isLive={matchState === 'live' && !isPaused} homeScore={homeScore} awayScore={awayScore} />
            
            {/* PAUSE BUTTON */}
            {matchState === 'live' && (
              <Button onClick={debouncedPause} data-testid="pause-resume-btn" className={`w-full ${isPaused ? 'bg-[#32D74B] hover:bg-[#32D74B]/80 text-black' : 'bg-[#FFD60A] hover:bg-[#FFD60A]/80 text-black'} font-bold text-lg py-3`}>
                {isPaused ? <><Play size={24} className="mr-2" weight="fill" /> RESUME MATCH</> : <><Pause size={24} className="mr-2" weight="fill" /> PAUSE MATCH</>}
              </Button>
            )}
            {isPaused && <p className="text-center text-[#FFD60A] text-xs animate-pulse">Match paused — make subs or formation changes, then resume</p>}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-3 flex gap-2">
                {matchState === 'live' && (
                  <Button data-testid="home-sub-btn" onClick={() => {
                    if (simControlRef.current && !isPaused) { simControlRef.current.pause(); setIsPaused(true); }
                    setTimeout(() => { setSubTeam(homeTeam); setShowSubModal(true); }, 50);
                  }} disabled={subsUsed.home >= 5} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white text-sm" size="sm">
                    <ArrowsLeftRight size={14} className="mr-1" /> Home Sub ({5 - subsUsed.home})
                  </Button>
                )}
              </div>
              <div className="lg:col-span-6">
                <MatchEvents events={events} currentMinute={currentMinute} isLive={matchState === 'live' && !isPaused} />
              </div>
              <div className="lg:col-span-3 flex gap-2">
                {matchState === 'live' && (
                  <Button data-testid="away-sub-btn" onClick={() => {
                    if (simControlRef.current && !isPaused) { simControlRef.current.pause(); setIsPaused(true); }
                    setTimeout(() => { setSubTeam(awayTeam); setShowSubModal(true); }, 50);
                  }} disabled={subsUsed.away >= 5} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white text-sm" size="sm">
                    <ArrowsLeftRight size={14} className="mr-1" /> Away Sub ({5 - subsUsed.away})
                  </Button>
                )}
              </div>
            </div>
            {matchState === 'finished' && (
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="share-card-btn" onClick={handleShareCard} className="bg-[#D4A933] hover:bg-[#F0D07A] text-black font-heading font-bold uppercase tracking-wider">
                  <ShareNetwork size={14} weight="bold" className="mr-1" /> Share Card
                </Button>
                <Button data-testid="new-match-btn" onClick={resetMatch} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white">New Match</Button>
              </div>
            )}
          </div>
        )}

        {/* Quick sim result */}
        {simMode === 'quick' && matchState !== 'setup' && homeTeam && awayTeam && (
          <>
            <div className="card-surface rounded-sm p-6 mb-6 text-center">
              <div className="flex items-center justify-center gap-6">
                <span className="font-heading font-bold text-xl text-white">{homeTeam.name}</span>
                <span className="score-display text-5xl text-white">{homeScore} - {awayScore}</span>
                <span className="font-heading font-bold text-xl text-white">{awayTeam.name}</span>
              </div>
              {matchState === 'finished' && <p className="text-[#A0A0A5] mt-2 text-sm">FULL TIME</p>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-3"><TacticalPitch team={homeTeam} /></div>
              <div className="lg:col-span-6">
                <MatchEvents events={events} currentMinute={currentMinute} isLive={matchState === 'live'} />
                {matchState === 'finished' && (
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Button data-testid="share-card-btn" onClick={handleShareCard} className="bg-[#D4A933] hover:bg-[#F0D07A] text-black font-heading font-bold uppercase tracking-wider">
                      <ShareNetwork size={14} weight="bold" className="mr-1" /> Share Card
                    </Button>
                    <Button data-testid="new-match-btn" onClick={resetMatch} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white">New Match</Button>
                  </div>
                )}
              </div>
              <div className="lg:col-span-3"><TacticalPitch team={awayTeam} isAway /></div>
            </div>
          </>
        )}

        {/* Setup */}
        {matchState === 'setup' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase tracking-wide">Home Team</h3></div>
                <div className="p-2 border-b border-[#2C2C35] grid grid-cols-3 gap-1">
                  {Object.entries(POOL_LABELS).map(([k, v]) => (
                    <button key={k} data-testid={`home-pool-${k}`}
                      onClick={() => { setHomePool(k); setHomeTeam(null); }}
                      style={homePool === k ? { backgroundColor: v.c, color: '#fff' } : {}}
                      className={`px-1.5 py-1 text-[10px] font-heading uppercase rounded-sm transition-all ${homePool === k ? 'shadow-[0_0_8px_rgba(255,215,0,0.4)]' : 'text-[#A0A0A5] hover:text-white'}`}>
                      {v.l}
                    </button>
                  ))}
                </div>
                <div className="p-2 border-b border-[#2C2C35]">
                  <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
                    <Input data-testid="home-search" value={homeSearch} onChange={(e) => setHomeSearch(e.target.value)} placeholder="Search teams..." className="pl-7 h-8 text-xs bg-[#0A0A0C] border-[#2C2C35] text-white" />
                  </div>
                </div>
                <ScrollArea className="h-[350px]">
                  <div key={`home-${homePool}`} className="p-2 space-y-2">
                    {filterTeams(getPool(homePool), homeSearch).map(t => (
                      <TeamCardMini key={t.id} team={t} onClick={setHomeTeam} isSelected={homeTeam?.id === t.id} />
                    ))}
                    {filterTeams(getPool(homePool), homeSearch).length === 0 && (
                      <p className="text-[#6E6E73] text-xs text-center py-6">No teams match.</p>
                    )}
                  </div>
                </ScrollArea>
                <div className="border-t border-[#2C2C35] px-3 py-1.5 text-[10px] text-[#6E6E73] uppercase tracking-wider">{filterTeams(getPool(homePool), homeSearch).length} teams</div>
              </div>
            </div>
            <div className="lg:col-span-6 space-y-4">
              {homeTeam && awayTeam && (
                <div className="card-surface rounded-sm p-6 text-center relative overflow-hidden">
                  {/* Futuristic scanline backdrop */}
                  <div className="absolute inset-0 opacity-[0.07] pointer-events-none bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,#FFD60A_2px,#FFD60A_3px)]" />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C8102E]/10 via-transparent to-[#007AFF]/10 pointer-events-none" />
                  <div className="relative flex items-center justify-center gap-8">
                    <div data-testid="playnow-vs-home">
                      <div className="w-20 h-20 mx-auto"><TeamLogo team={homeTeam} size={80} /></div>
                      <p className="font-heading font-bold text-white mt-2">{homeTeam.name}</p>
                      <p className="text-sm text-[#6E6E73]">OVR: {getTeamOverall(homeTeam)}</p>
                    </div>
                    <div className="text-center">
                      <span className="font-heading text-4xl text-[#FFD60A] tracking-widest drop-shadow-[0_0_8px_#FFD60A] block">VS</span>
                      <span className="text-[9px] text-[#6E6E73] uppercase tracking-[0.3em] mt-1 block">Friendly</span>
                    </div>
                    <div data-testid="playnow-vs-away">
                      <div className="w-20 h-20 mx-auto"><TeamLogo team={awayTeam} size={80} /></div>
                      <p className="font-heading font-bold text-white mt-2">{awayTeam.name}</p>
                      <p className="text-sm text-[#6E6E73]">OVR: {getTeamOverall(awayTeam)}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="card-surface rounded-sm p-4 space-y-4">
                <div><label className="text-xs font-bold uppercase text-[#6E6E73] tracking-wider mb-2 block">Full Sim Speed</label>
                  <div className="flex items-center gap-4"><Timer size={16} className="text-[#6E6E73]" /><Slider value={simSpeed} onValueChange={setSimSpeed} min={20} max={200} step={10} className="flex-1" /><span className="font-mono text-sm text-[#A0A0A5] w-16">{simSpeed[0]}ms</span></div>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="quick-sim-btn" onClick={debouncedQuick} disabled={!homeTeam || !awayTeam} className="flex-1 bg-[#FF3B30] hover:bg-[#FF5C53] text-white"><Lightning size={18} className="mr-2" weight="fill" />Quick Sim</Button>
                  <Button data-testid="full-sim-btn" onClick={debouncedFull} disabled={!homeTeam || !awayTeam} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white"><Play size={18} className="mr-2" weight="fill" />Full Sim</Button>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase tracking-wide">Away Team</h3></div>
                <div className="p-2 border-b border-[#2C2C35] grid grid-cols-3 gap-1">
                  {Object.entries(POOL_LABELS).map(([k, v]) => (
                    <button key={k} data-testid={`away-pool-${k}`}
                      onClick={() => { setAwayPool(k); setAwayTeam(null); }}
                      style={awayPool === k ? { backgroundColor: v.c, color: '#fff' } : {}}
                      className={`px-1.5 py-1 text-[10px] font-heading uppercase rounded-sm transition-all ${awayPool === k ? 'shadow-[0_0_8px_rgba(255,215,0,0.4)]' : 'text-[#A0A0A5] hover:text-white'}`}>
                      {v.l}
                    </button>
                  ))}
                </div>
                <div className="p-2 border-b border-[#2C2C35]">
                  <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
                    <Input data-testid="away-search" value={awaySearch} onChange={(e) => setAwaySearch(e.target.value)} placeholder="Search teams..." className="pl-7 h-8 text-xs bg-[#0A0A0C] border-[#2C2C35] text-white" />
                  </div>
                </div>
                <ScrollArea className="h-[350px]">
                  <div key={`away-${awayPool}`} className="p-2 space-y-2">
                    {filterTeams(getPool(awayPool).filter(t => t.id !== homeTeam?.id), awaySearch).map(t => (
                      <TeamCardMini key={t.id} team={t} onClick={setAwayTeam} isSelected={awayTeam?.id === t.id} />
                    ))}
                    {filterTeams(getPool(awayPool).filter(t => t.id !== homeTeam?.id), awaySearch).length === 0 && (
                      <p className="text-[#6E6E73] text-xs text-center py-6">No teams match.</p>
                    )}
                  </div>
                </ScrollArea>
                <div className="border-t border-[#2C2C35] px-3 py-1.5 text-[10px] text-[#6E6E73] uppercase tracking-wider">{filterTeams(getPool(awayPool).filter(t => t.id !== homeTeam?.id), awaySearch).length} teams</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <SubstitutionModal isOpen={showSubModal} onClose={() => { setShowSubModal(false); setSubTeam(null); }} team={subTeam} onSubstitute={handleSub} subsRemaining={subTeam ? (subTeam.id === homeTeam?.id ? 5 - subsUsed.home : 5 - subsUsed.away) : 5} />
    </div>
  );
};
